import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, MessageCircle, Loader2, Minus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { getSocket } from '@/lib/socket';
import { useChatStore } from '@/store/chat.store';
import { getInitials } from '@/lib/utils';
import { ChatConversacion } from './ChatConversacion';

interface ConversacionItem {
  conversacionKey: string;
  otroMedico: {
    id: string;
    nombre: string;
    especialidad?: { nombre: string };
    usuario?: { fotoUrl?: string };
  };
  ultimoMensaje: {
    contenido?: string;
    tipo: 'texto' | 'imagen' | 'archivo';
    createdAt: string;
    remitenteId: string;
  };
  noLeidos: number;
}

export function ChatPanel() {
  const { open, medicoId, closeChat, goBack, openChat } = useChatStore();
  const [minimizado, setMinimizado] = useState(false);
  const qc = useQueryClient();
  const socket = getSocket();

  // Restaurar al abrir un chat nuevo
  useEffect(() => { if (medicoId) setMinimizado(false); }, [medicoId]);

  const { data: conversaciones = [], isLoading } = useQuery<ConversacionItem[]>({
    queryKey: ['chat-conversaciones'],
    queryFn: () => api.get('/api/chat/conversaciones').then(r => r.data),
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    function onMensaje() { qc.invalidateQueries({ queryKey: ['chat-conversaciones'] }); }
    socket.on('chat:mensaje', onMensaje);
    return () => { socket.off('chat:mensaje', onMensaje); };
  }, [open, socket, qc]);

  if (!open) return null;

  const noLeidosTotal = conversaciones.reduce((acc, c) => acc + c.noLeidos, 0);
  const medicoActivo = medicoId
    ? conversaciones.find(c => c.otroMedico.id === medicoId)?.otroMedico
    : null;

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={closeChat} />
    <div
      className={cn(
        'fixed z-50 flex flex-col',
        'bg-white shadow-2xl border border-gray-100',
        'transition-all duration-200',
        // Desktop: floating panel bottom-right
        'md:bottom-4 md:right-[76px] md:w-[360px] md:rounded-2xl',
        // Mobile: bottom sheet full width
        'bottom-0 left-0 right-0 rounded-t-3xl',
        minimizado
          ? 'md:h-auto h-auto'
          : medicoId
          ? 'md:h-[520px] h-[82vh]'
          : 'md:h-[460px] h-[82vh]',
      )}
      style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 flex-shrink-0 cursor-pointer select-none rounded-t-2xl"
        style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)' }}
        onClick={() => setMinimizado(m => !m)}
      >
        {medicoId && medicoActivo ? (
          <>
            <button
              onClick={e => { e.stopPropagation(); goBack(); }}
              className="p-1 rounded-lg hover:bg-white/20 text-white/80 transition-colors flex-shrink-0"
              title="Volver"
            >
              <MessageCircle size={15} />
            </button>
            <div className="w-7 h-7 rounded-full flex-shrink-0 overflow-hidden bg-white/20 flex items-center justify-center text-white text-[10px] font-bold">
              {medicoActivo.usuario?.fotoUrl
                ? <img src={medicoActivo.usuario.fotoUrl} alt="" className="w-full h-full object-cover" />
                : getInitials(medicoActivo.nombre)
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight truncate">Dr. {medicoActivo.nombre}</p>
              {medicoActivo.especialidad && (
                <p className="text-white/60 text-[10px] leading-tight truncate">{medicoActivo.especialidad.nombre}</p>
              )}
            </div>
          </>
        ) : (
          <>
            <MessageCircle size={16} className="text-white/80 flex-shrink-0" />
            <span className="flex-1 text-white text-sm font-semibold">
              Mensajes
              {noLeidosTotal > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                  {noLeidosTotal > 9 ? '9+' : noLeidosTotal}
                </span>
              )}
            </span>
          </>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={e => { e.stopPropagation(); setMinimizado(m => !m); }}
            className="p-1 rounded-lg hover:bg-white/20 text-white/70 transition-colors"
          >
            <Minus size={14} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); closeChat(); }}
            className="p-1 rounded-lg hover:bg-white/20 text-white/70 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* ── Contenido (oculto si minimizado) ────────────────────────────── */}
      {!minimizado && (
        <div className="flex-1 overflow-hidden flex flex-col rounded-b-2xl">
          {medicoId ? (
            <ChatConversacion medicoId={medicoId} onBack={goBack} />
          ) : (
            <ConversacionesList
              conversaciones={conversaciones}
              isLoading={isLoading}
              onSelect={openChat}
            />
          )}
        </div>
      )}
    </div>
    </>
  );
}

function ConversacionesList({
  conversaciones,
  isLoading,
  onSelect,
}: {
  conversaciones: ConversacionItem[];
  isLoading: boolean;
  onSelect: (medicoId: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-1 py-10">
        <Loader2 size={22} className="animate-spin text-gray-300" />
      </div>
    );
  }

  if (conversaciones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-center px-8 py-10">
        <MessageCircle size={40} className="text-gray-200 mb-3" />
        <p className="text-sm font-medium text-text-primary">Sin conversaciones</p>
        <p className="text-xs text-text-secondary mt-1">
          Inicia un chat desde "Mi Red" con un colega
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto flex-1 divide-y divide-gray-50">
      {conversaciones.map(c => {
        const foto = c.otroMedico.usuario?.fotoUrl ?? null;
        const preview = c.ultimoMensaje.tipo === 'texto'
          ? (c.ultimoMensaje.contenido ?? '')
          : c.ultimoMensaje.tipo === 'imagen'
            ? '📷 Imagen'
            : '📎 Archivo adjunto';

        return (
          <button
            key={c.conversacionKey}
            onClick={() => onSelect(c.otroMedico.id)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
          >
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                {foto
                  ? <img src={foto} alt={c.otroMedico.nombre} className="w-full h-full object-cover" />
                  : getInitials(c.otroMedico.nombre)
                }
              </div>
              {c.noLeidos > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {c.noLeidos > 9 ? '9+' : c.noLeidos}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-1">
                <p className={cn(
                  'text-sm truncate',
                  c.noLeidos > 0 ? 'font-bold text-text-primary' : 'font-medium text-text-primary',
                )}>
                  Dr. {c.otroMedico.nombre}
                </p>
                <span className="text-[10px] text-gray-400 flex-shrink-0">
                  {formatDistanceToNow(new Date(c.ultimoMensaje.createdAt), { locale: es, addSuffix: false })}
                </span>
              </div>
              <p className={cn(
                'text-xs truncate mt-0.5',
                c.noLeidos > 0 ? 'text-text-primary font-medium' : 'text-text-secondary',
              )}>
                {preview}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
